import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import { RegulatoryData } from '../../useProductAnalysis.logic';
import { AIInsightItem } from 'Interfaces/ProductWikiInterfaces/aiInsights.types';
import { productWikiServices } from 'services/productWikiServices';

const useRegulatoryTab = ({
  productAnalysisStore,
  regulatoryData: propRegulatoryData,
}: {
  productAnalysisStore: ProductAnalysisStore;
  regulatoryData: RegulatoryData;
}) => {
  const [searchParams] = useSearchParams();
  const gfc = searchParams.get('gfc');
  const [regulatoryFilters, setRegulatoryFilters] = useState<any>({});

  // Get regulatory data from store (preferred) or fallback to props
  const storeRegulatoryData = productAnalysisStore.regulatoryData;
  const regulatoryData = storeRegulatoryData || propRegulatoryData;

  const [localCountryMatrix, _setLocalCountryMatrix] = useState(regulatoryData?.countryMatrix || []);
  const [taaSummary, _setTaaSummary] = useState(
    regulatoryData?.taaSummary || {
      taaPercentage: 0,
      taaCountry: '',
      nonTaaPercentage: 0,
      nonTaaCountry: '',
      summaryText: 'No data available',
    }
  );
  const isLoading = productAnalysisStore.isRegulatoryLoading;

  // AI Insights state
  const [aiInsights, setAiInsights] = useState<AIInsightItem[] | null>(null);
  const [isInsightsLoading, setIsInsightsLoading] = useState<boolean>(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const insightsAbortControllerRef = useRef<AbortController | null>(null);

  // Fetch regulatory data when component mounts or when GFC changes
  // useEffect(() => {
  //   if (gfc && gfc.trim() !== '') {
  //     productAnalysisStore.fetchRegulatoryData({ gfc: gfc.trim() });
  //   }
  // }, [gfc]);

  // Fetch AI Insights for regulatory strategy recommendations
  const fetchAIInsights = useCallback(async () => {
    if (!gfc || gfc.trim() === '') {
      setAiInsights(null);
      return;
    }

    // Abort previous insights request if it exists
    if (insightsAbortControllerRef.current) {
      insightsAbortControllerRef.current.abort();
      insightsAbortControllerRef.current = null;
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    insightsAbortControllerRef.current = abortController;

    setIsInsightsLoading(true);
    setInsightsError(null);

    try {
      const response = await productWikiServices.generateAIInsights(
        {
          insight_type: 'regulatory',
          gfc: gfc.trim(),
          period_option: '12 months',
          llm_backend: "anthropic",
        },
        abortController.signal
      );

      if (response.data?.success && response.data?.insights) {
        setAiInsights(response.data.insights);
      } else {
        setAiInsights(null);
        if (response.data?.error || response.data?.message) {
          // Combine error and message for a complete error display
          const errorParts: string[] = [];
          if (response.data.error) errorParts.push(response.data.error);
          if (response.data.message && response.data.message !== response.data.error) {
            errorParts.push(response.data.message);
          }
          setInsightsError(errorParts.join(' - ') || 'Failed to load insights');
        }
      }
    } catch (error) {
      // Don't set error if request was aborted
      if (error instanceof Error && error.name === 'CanceledError') {
        return;
      }
      setInsightsError('Failed to load AI insights');
      setAiInsights(null);
    } finally {
      setIsInsightsLoading(false);
    }
  }, [gfc]);

  // Effect to fetch AI insights when GFC changes
  useEffect(() => {
    fetchAIInsights();

    // Cleanup: abort request when effect re-runs or component unmounts
    return () => {
      if (insightsAbortControllerRef.current) {
        insightsAbortControllerRef.current.abort();
        insightsAbortControllerRef.current = null;
      }
    };
  }, [fetchAIInsights]);

  // Initialize local state when regulatoryData changes (from store or props)
  // useEffect(() => {
  //   if (regulatoryData && regulatoryData.countryMatrix) {
  //     setLocalCountryMatrix(regulatoryData.countryMatrix);
  //     setTaaSummary(
  //       regulatoryData.taaSummary || {
  //         taaPercentage: 0,
  //         taaCountry: '',
  //         nonTaaPercentage: 0,
  //         nonTaaCountry: '',
  //         summaryText: 'No data available',
  //       }
  //     );
  //   }
  // }, [regulatoryData?.countryMatrix, regulatoryData?.taaSummary]);

  // Calculate TAA summary dynamically when localCountryMatrix changes
  // useEffect(() => {
  //   const calculateTaaSummary = () => {
  //     if (!localCountryMatrix || localCountryMatrix.length === 0) {
  //       setTaaSummary({
  //         taaPercentage: 0,
  //         taaCountry: '',
  //         nonTaaPercentage: 0,
  //         nonTaaCountry: '',
  //         summaryText: 'No data available',
  //       });
  //       return;
  //     }

  //     // Separate TAA and Non-TAA records
  //     const taaRecords: Array<{ volumeShare: number; apiSource: string }> = [];
  //     const nonTaaRecords: Array<{ volumeShare: number; apiSource: string }> = [];

  //     localCountryMatrix.forEach(record => {
  //       const volumeShareStr = record.volumeShare || '0%';
  //       const volumeShare = parseFloat(volumeShareStr.replace('%', '')) || 0;
  //       const apiSource = record.apiSource || '';
  //       const taaStatus = record.taaStatus;

  //       if (taaStatus) {
  //         taaRecords.push({ volumeShare, apiSource });
  //       } else {
  //         nonTaaRecords.push({ volumeShare, apiSource });
  //       }
  //     });

  //     // Calculate sum of volume shares for TAA
  //     let taaPercentage = 0;
  //     let taaCountry = '';
  //     if (taaRecords.length > 0) {
  //       taaPercentage = taaRecords.reduce((sum, r) => sum + r.volumeShare, 0);
  //       // Find API Source with maximum volume share for TAA
  //       const maxTaaRecord = taaRecords.reduce((max, r) =>
  //         r.volumeShare > max.volumeShare ? r : max
  //       );
  //       taaCountry = maxTaaRecord.apiSource;
  //     }

  //     // Calculate sum of volume shares for Non-TAA
  //     let nonTaaPercentage = 0;
  //     let nonTaaCountry = '';
  //     if (nonTaaRecords.length > 0) {
  //       nonTaaPercentage = nonTaaRecords.reduce((sum, r) => sum + r.volumeShare, 0);
  //       // Find API Source with maximum volume share for Non-TAA
  //       const maxNonTaaRecord = nonTaaRecords.reduce((max, r) =>
  //         r.volumeShare > max.volumeShare ? r : max
  //       );
  //       nonTaaCountry = maxNonTaaRecord.apiSource;
  //     }

  //     // Build summary text
  //     const taaText = `${taaPercentage.toFixed(1)}% volume from TAA${taaCountry ? ` (${taaCountry})` : ''}`;
  //     const nonTaaText = `${nonTaaPercentage.toFixed(1)}% volume from Non-TAA${nonTaaCountry ? ` (${nonTaaCountry})` : ''}`;
  //     const summaryText = `${taaText} | ${nonTaaText}`;

  //     setTaaSummary({
  //       taaPercentage: Math.round(taaPercentage * 10) / 10,
  //       taaCountry,
  //       nonTaaPercentage: Math.round(nonTaaPercentage * 10) / 10,
  //       nonTaaCountry,
  //       summaryText,
  //     });
  //   };

  //   calculateTaaSummary();
  // }, [localCountryMatrix]);

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'low':
        return '#198754';
      case 'medium':
        return '#ffc107';
      case 'high':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getComplianceScoreClass = (score: number) => {
    if (score >= 70) return 'high';
    if (score >= 50) return 'moderate';
    return 'low';
  };

  // Custom filter function for TAA Status to filter by label text
  const _taaStatusFilterFunction = (value: boolean, filter: string) => {
    if (!filter) return true;
    const label = value ? 'TAA' : 'Non-TAA';
    return label.toLowerCase().includes(filter.toLowerCase());
  };

  // Initialize filters for Regulatory DataTable
  // useEffect(() => {
  //   const initialFilters: any = {
  //     global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  //   };

  //   // Register custom filter for TAA Status
  //   FilterService.register('custom_taaStatus', taaStatusFilterFunction);

  //   // Initialize filters for each column
  //   initialFilters['manufacturer'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
  //   initialFilters['apiSource'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
  //   initialFilters['fdfManufacturing'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
  //   initialFilters['taaStatus'] = { value: null, matchMode: FilterMatchMode.CUSTOM };
  //   initialFilters['volumeShare'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
  //   initialFilters['fss'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
  //   initialFilters['dapa'] = { value: null, matchMode: FilterMatchMode.CONTAINS };
  //   initialFilters['riskLevel'] = { value: null, matchMode: FilterMatchMode.CONTAINS };

  //   setRegulatoryFilters(initialFilters);
  // }, []);

  return {
    regulatoryFilters,
    setRegulatoryFilters,
    getRiskLevelColor,
    getComplianceScoreClass,
    isLoading,
    taaSummary,
    localCountryMatrix,
    regulatoryData,
    // AI Insights
    aiInsights,
    isInsightsLoading,
    insightsError,
    refetchInsights: fetchAIInsights,
  };
};

export default useRegulatoryTab;
